import { useState, useEffect } from "react";
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DateRange from '../selectComponents/DateRange';
import SmrySelect from '../selectComponents/SmrySelect';
import ColorSelect from '../selectComponents/ColorSelect';
import GridSelect from '../selectComponents/GridSelect';
import { FieldsetBox, LegendStyled } from '../styling/StyledComponents';
import InfoButton from '../selectComponents/InfoButton';
import { checkGridAvailable } from '../js/utilityRoutines';

const variableChoices = [
  {value: 'maxt', label: 'Max temperature', units: 'degF'},
  {value: 'mint', label: 'Min temperature', units: 'degF'},
  {value: 'avgt', label: 'Avg temperature', units: 'degF'},
  {value: 'pcpn', label: 'Precipitation', units: 'inch'},
  {value: 'hdd', label: 'Heating Degree Days', units: 'degF'},
  {value: 'cdd', label: 'Cooling Degree Days', units: 'degF'},
]
const smryChoices = [
  {value: 'max', label: 'Highest'},
  {value: 'min', label: 'Lowest'},
  {value: 'mean', label: 'Mean'},
  {value: 'sum', label: 'Sum'},
  {value: 'cntdys', label: 'Number of days'},
]
const interpChoices = [
  {value: 'none', label: 'None'},
  {value: 'cspline', label: 'Cubic Spline',},
]

export default function GridmapOptions(props) {
  const { userParams, setUserParams, selectOptions, handleChange, setDisableGoButton } = props;
  const [ clevelsError, setClevelsError ] = useState(Array(10).fill(false));
  const [ gridError, setGridError ] = useState("");
  const [ gridEndDate, setGridEndDate ] = useState({});
  
  const handleClevelChange = (index) => event => {
    const newbin = event.target.value ? event.target.value : "";
    const sbins = userParams.clevels;
    const sbinserror = Array(sbins.length).fill(false);
    sbins[index] = (newbin !== "" && !isNaN(newbin)) ? newbin : "";
    const checkArray = sbins.filter((e) => { return e === 0 || e });  //remove falsy, but not zero
    checkArray.forEach((sb, i) => {
      const sb_previous = i === 0 ? -99999 : checkArray[i-1];
      const sb_next = i === checkArray.length ? 99999 : checkArray[i+1];
      const index = sbins.lastIndexOf(sb);
      sbinserror[index] = (parseFloat(sb) <= parseFloat(sb_previous) || parseFloat(sb) >= parseFloat(sb_next));
    })
    setUserParams({ ...userParams, clevels: sbins});
    setClevelsError(sbinserror);
    setDisableGoButton(sbinserror.indexOf(true) >= 0 ? true : false);
  }

  const clearLevels = () => {
    setUserParams({ ...userParams, clevels: Array(10).fill(""), colors: [], cmap: ""});
    setClevelsError(Array(10).fill(false));  
    setDisableGoButton(false);  
  }

  const handleCheckboxChange = (event) => {
    const name = event.target.name;
		const checked = event.target.checked;
    const overlayOptions = {...userParams.overlays, [name]: checked};
    setUserParams({...userParams, overlays:overlayOptions})
  }

  useEffect(() => {
    const varUnits = variableChoices.find(x => x.value === userParams.variable).units;
    if (varUnits !== userParams.units) {
      setUserParams({ ...userParams, units: varUnits})
    }
  }, [userParams.variable]);

  useEffect(() => {
    checkGridAvailable(userParams, setGridError, setDisableGoButton, gridEndDate, setGridEndDate);
  }, [userParams.toDate, userParams.fromDate, userParams.variable, userParams.area]);

  return (
    <>
      <DateRange 
        userParams={userParams}
        setUserParams={setUserParams}
      />
      <GridSelect 
        userParams={userParams}
        setUserParams={setUserParams} 
        selectOptions={selectOptions} 
        setDisableGoButton={setDisableGoButton}
        gridError={gridError}
        setGridError={setGridError}
        gridEndDate={gridEndDate}
        setGridEndDate={setGridEndDate}
      />
      <SmrySelect 
        userParams={userParams}
        setUserParams={setUserParams} 
        selectOptions={selectOptions} 
        handleChange={handleChange} 
        setDisableGoButton={setDisableGoButton} 
        smryChoices={smryChoices} 
        variableChoices={variableChoices} 
      />
      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 1 }}>
        <TextField
          select
          name="interp"
          label="Interpolation"
          value={userParams.interp}
          onChange={handleChange}
          InputProps={{sx: {height: "3em"}, endAdornment: <InfoButton adornment="end" infoFor='interp' />}}
          sx={{ "& .MuiSelect-icon": { right: '28px' } }}
        >
          {selectOptions(interpChoices)}
        </TextField>
        <FieldsetBox component="fieldset">
          <LegendStyled>Map overlay outlines</LegendStyled>
          <FormGroup row>
            { Object.keys(userParams.overlays).map((opt, i) => (
                <FormControlLabel
                    key={i}
                    control={
                        <Checkbox checked={userParams.overlays[opt]} name={opt} sx={{ py: 0 }} onChange={handleCheckboxChange} />
                    }
                    label={opt.charAt(0).toUpperCase() + opt.slice(1)}
                />
            ))}
          </FormGroup>
        </FieldsetBox>
      </Stack>
      <FieldsetBox component="fieldset" sx={{ mt: 1 }}>
        <LegendStyled>Optional - Contour levels ({userParams.units})</LegendStyled>
        <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
          Leave blank for auto-selection
        </Typography>
        {userParams.clevels.map((cl, k) => 
          <TextField
            key={k}
            value={cl}
            onChange={handleClevelChange(k)}
            sx={{ width: "5ch",  mt: 0, mb: 0, ml: 1 }} 
            error={clevelsError[k]}
            inputProps={{sx: { p: "0.25em"}}}
          />
        )}
        <Button
          onClick={clearLevels}
          sx={{ ml: 1 }}
        >
          Clear
        </Button>
      </FieldsetBox>
      <ColorSelect
        userParams={userParams}
        setUserParams={setUserParams}
      />
    </>
  )
}
